import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeSimple, Phone, MapPin, CaretDown, CaretUp, LinkedinLogo, InstagramLogo, FacebookLogo, YoutubeLogo, TwitterLogo } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Social icon mapping
const socialIcons = {
  linkedin: LinkedinLogo,
  instagram: InstagramLogo,
  facebook: FacebookLogo,
  youtube: YoutubeLogo,
  twitter: TwitterLogo,
};

const defaultSocialLinks = [
  { platform: 'linkedin', url: 'https://linkedin.com/company/ihgindustry', enabled: true },
  { platform: 'instagram', url: 'https://instagram.com/ihgindustry', enabled: true },
  { platform: 'facebook', url: 'https://facebook.com/ihgindustry', enabled: true },
  { platform: 'youtube', url: 'https://youtube.com/@ihgindustry', enabled: true },
];

const defaultFooterLinks = {
  capabilities: [
    { label: 'FACADES', href: '/capabilities/architectural-metal-facades' },
    { label: 'INTERIORS', href: '/capabilities/interior-metal-decor' },
    { label: 'FINISHES', href: '/capabilities/special-metal-finishes' },
    { label: 'CNC & LASER', href: '/capabilities/cnc-laser-cutting' },
  ],
  studio: [
    { label: 'ARCHITECTURAL', href: '/studio-works/architectural' },
    { label: 'INTERIORS', href: '/studio-works/interiors' },
    { label: 'ART ATELIER', href: '/art-atelier' },
    { label: 'RETAIL', href: '/studio-works/retail' },
  ],
  resources: [
    { label: 'PROJECTS', href: '/projects' },
    { label: 'PORTFOLIO', href: '/portfolio' },
    { label: 'MATERIALS', href: '/finishes' },
    { label: 'DOWNLOADS', href: '/downloads' },
    { label: 'CAD/BIM LIBRARY', href: '/resources' },
    { label: 'KNOWLEDGE BASE', href: '/knowledge-base' },
    { label: 'JOURNAL', href: '/insights' },
  ],
  company: [
    { label: 'ABOUT', href: '/about' },
    { label: 'COMPANY PROFILE', href: '/company-profile' },
    { label: 'TEAM', href: '/team' },
    { label: 'CAREERS', href: '/careers' },
    { label: 'CONTACT', href: '/collaborate' },
  ],
  africa: [
    { label: 'KENYA', href: '/africa/kenya' },
    { label: 'NIGERIA', href: '/africa/nigeria' },
    { label: 'ETHIOPIA', href: '/africa/ethiopia' },
    { label: 'SOUTH AFRICA', href: '/africa/south-africa' },
    { label: 'GHANA', href: '/africa/ghana' },
    { label: 'TANZANIA', href: '/africa/tanzania' },
  ]
};

export const Footer = () => {
  const [footerLinks, setFooterLinks] = useState(defaultFooterLinks);
  const [socialLinks, setSocialLinks] = useState(defaultSocialLinks);
  const [siteConfig, setSiteConfig] = useState({
    phone: '+971 4 XXX XXXX',
    email: 'info@ihgind.com'
  });
  const [africaExpanded, setAfricaExpanded] = useState(false);

  useEffect(() => {
    // Fetch footer content from API and merge over the hardcoded defaults so
    // newly-added links (e.g. Projects, Company Profile) always render even if
    // the CMS document is stale.
    axios.get(`${API}/api/footer-content`)
      .then(res => {
        if (res.data?.columns?.length > 0) {
          const apiLinksByCol = {};
          res.data.columns.forEach(col => {
            apiLinksByCol[col.id || col.title.toLowerCase()] = col.links || [];
          });
          setFooterLinks(prev => {
            const merged = { ...prev };
            Object.keys(apiLinksByCol).forEach((colKey) => {
              const apiLinks = apiLinksByCol[colKey] || [];
              const defaultLinks = prev[colKey] || [];
              const seen = new Set(apiLinks.map((l) => (l.href || '').toLowerCase()));
              // Keep every API link (CMS wins on ordering + label), then append
              // any hard-coded default whose href isn't already in the API list.
              const missingDefaults = defaultLinks.filter(
                (l) => !seen.has((l.href || '').toLowerCase())
              );
              merged[colKey] = [...apiLinks, ...missingDefaults];
            });
            return merged;
          });
        }
        // Set social links if available
        if (res.data?.socialLinks) {
          setSocialLinks(res.data.socialLinks);
        }
      })
      .catch(() => {});
    
    // Fetch site config for contact info
    axios.get(`${API}/api/site-config`)
      .then(res => {
        if (res.data) {
          setSiteConfig(res.data);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <footer className="bg-obsidian text-white" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Main Footer - Mobile: 2 cols, Desktop: 5 cols */}
        <div className="py-8 md:py-10 grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {/* Capabilities */}
          <div>
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em] mb-2 md:mb-3">
              CAPABILITIES
            </h4>
            <nav className="flex flex-col gap-0.5 md:gap-1">
              {footerLinks.capabilities.map((link) => (
                <Link 
                  key={`${link.href}|${link.label}`} 
                  to={link.href}
                  className="font-body text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Studio */}
          <div>
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em] mb-2 md:mb-3">
              STUDIO
            </h4>
            <nav className="flex flex-col gap-0.5 md:gap-1">
              {footerLinks.studio.map((link) => (
                <Link 
                  key={`${link.href}|${link.label}`} 
                  to={link.href}
                  className="font-body text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em] mb-2 md:mb-3">
              RESOURCES
            </h4>
            <nav className="flex flex-col gap-0.5 md:gap-1">
              {footerLinks.resources.map((link) => (
                <Link 
                  key={`${link.href}|${link.label}`} 
                  to={link.href}
                  className="font-body text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em] mb-2 md:mb-3">
              COMPANY
            </h4>
            <nav className="flex flex-col gap-0.5 md:gap-1">
              {footerLinks.company.map((link) => (
                <Link 
                  key={`${link.href}|${link.label}`} 
                  to={link.href}
                  className="font-body text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em] mb-2 md:mb-3">
              CONTACT
            </h4>
            <div className="flex flex-col gap-1">
              {siteConfig.address && (
                <div className="flex items-start gap-1.5 text-[10px] md:text-[11px] text-white/60 uppercase tracking-wider py-0.5" data-testid="footer-address">
                  <MapPin weight="bold" className="w-4 h-4 text-bronze flex-shrink-0 mt-0.5" />
                  <span>{siteConfig.address.toUpperCase()}</span>
                </div>
              )}
              <a 
                href={`mailto:${siteConfig.email}`}
                aria-label={`Email us at ${siteConfig.email}`}
                className="flex items-center gap-1.5 text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
              >
                <EnvelopeSimple weight="bold" className="w-4 h-4 text-bronze flex-shrink-0" />
                <span className="truncate">{siteConfig.email?.toUpperCase() || 'INFO@IHGIND.COM'}</span>
              </a>
              <a 
                href={`tel:${siteConfig.phone?.replace(/\s/g, '')}`}
                aria-label={`Call us at ${siteConfig.phone}`}
                className="flex items-center gap-1.5 text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
              >
                <Phone weight="bold" className="w-4 h-4 text-bronze flex-shrink-0" />
                {siteConfig.phone || '+971 4 258 5754'}
              </a>
            </div>
            
            {/* Social Icons - Under Contact (Dynamic) */}
            <div className="flex items-center gap-3 mt-4">
              {socialLinks.filter(s => s.enabled && s.url).map((social) => {
                const IconComponent = socialIcons[social.platform];
                if (!IconComponent) return null;
                return (
                  <a 
                    key={social.platform}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/40 hover:text-white transition-colors"
                    aria-label={social.platform}
                  >
                    <IconComponent weight="fill" className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Africa Markets - Expandable Row */}
        <div className="border-t border-white/10">
          <button
            onClick={() => setAfricaExpanded(!africaExpanded)}
            className="w-full py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          >
            <h4 className="font-display text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-[0.12em]">
              AFRICA MARKETS
            </h4>
            {africaExpanded ? (
              <CaretUp weight="bold" className="w-4 h-4 text-white/40" />
            ) : (
              <CaretDown weight="bold" className="w-4 h-4 text-white/40" />
            )}
          </button>
          {africaExpanded && (
            <nav className="pb-4 flex flex-wrap gap-x-4 gap-y-1">
              {footerLinks.africa.map((link) => (
                <Link 
                  key={`${link.href}|${link.label}`} 
                  to={link.href}
                  className="font-body text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors uppercase tracking-wider py-0.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="py-4 md:py-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="font-body text-[9px] md:text-[10px] text-white/40 uppercase tracking-[0.1em]">
            © {new Date().getFullYear()} IHG INDUSTRY {siteConfig.address ? `• ${siteConfig.address.split(',').pop()?.trim().toUpperCase()}` : '• DUBAI, UAE'}
          </p>
          <p className="font-body text-[9px] md:text-[10px] text-white/40 uppercase tracking-[0.1em]">
            DESIGN & DEVELOPED BY IHG MEDIA
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
